import { DataTableColumnHeader } from '@/components/dataTable/ColumnHeader';
import { getSerialNumber } from '@/components/dataTable/data-table.utils';
import { Button } from '@/design-system/button';
import { DeleteIcon, EditIcon, LockCloseIcon } from '@/icons';
import { formatDate } from '@/lib/dateFormatter';
import { ToggleStatusSubcategoryPayload } from '@/services/charges/charge.mutation';
import { ChargeSubcategory } from '@/services/charges/charges.types';
import { Link } from '@tanstack/react-router';
import { ColumnDef } from '@tanstack/react-table';
import { LockOpenIcon } from 'lucide-react';
import { ReactNode } from 'react';
import { ManageChargeSubCategoryContainer } from '../ManageChargeSubCategoryContainer';

type ModalSize = 'sm' | 'md' | 'lg';
type ModalVariant = 'default' | 'destructive' | 'warning';

type OnOpenProps = <T extends string>(config: {
  title: T;
  modalTitle: string | null;
  okText: string | ReactNode;
  component: ReactNode;
  cancelText?: string | ReactNode;
  size?: ModalSize;
  onCancel?: () => void;
}) => void;

type OpenConfirmationProps = (data: {
  title: string;
  description: string;
  cancelLabel: string;
  actionLabel: string;
  content?: React.ReactNode;
  size?: ModalSize;
  variant?: ModalVariant;
  onAction: () => Promise<void>;
  onCancel: () => void;
}) => void;

enum status {
  ACTIVE = 'A',
  BLOCK = 'B',
  DELETE = 'D',
}
export function getColumns(
  onOpen: OnOpenProps,
  openConfirmation: OpenConfirmationProps,
  t: (key: string) => string,
  toggleStatus: (payload: ToggleStatusSubcategoryPayload) => Promise<unknown>,
  categoryId?: string,
  agentType?: string,
): ColumnDef<ChargeSubcategory>[] {
  return [
    {
      accessorKey: 'chargeSubCategoryId',
      header: ({ column }) => (
        <DataTableColumnHeader className="whitespace-nowrap" column={column} title={t('common.page.tableColumn.sn')} />
      ),
      cell: ({ row, table }) => (
        <div className="font-medium truncate max-w-[10rem]">{getSerialNumber(table, row.index)}</div>
      ),
      enableSorting: false,
      enableHiding: false,
    },
    {
      accessorKey: 'categoryType',
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title={t('configurationManagement.charges.chargeSubCategory.table.categoryType')}
        />
      ),
      cell: ({ row }) => {
        const value = row.getValue<string>('categoryType');
        return row.original.status === status.ACTIVE ? (
          <Link
            params={{
              categoryId: row.original.chargeCategoryId,
            }}
            search={(previous) => {
              const agentType = row.original.agentType;
              const subCategoryId = row.original.chargeSubCategoryId;
              return {
                ...previous,
                agentType: agentType,
                subCategoryId: subCategoryId,
                breadcrumbReturnTo: window.location.href,
              };
            }}
            to="/configuration-management/charges/$categoryId/slabs"
          >
            <div className={`font-medium truncate max-w-[20rem] text-link underline`}>{value}</div>
          </Link>
        ) : (
          <div className={`font-medium truncate max-w-[20rem] text-link`}>{value}</div>
        );
      },
      enableHiding: false,
    },
    {
      accessorKey: 'description',
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title={t('configurationManagement.charges.chargeSubCategory.table.description')}
        />
      ),
      cell: ({ row }) => <div className="w-[332px] break-all">{row.getValue('description')}</div>,
      enableHiding: false,
    },
    {
      accessorKey: 'status',
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title={t('configurationManagement.charges.chargeSubCategory.table.status')}
        />
      ),
      cell: ({ row }) => {
        return (
          <div className="font-medium">
            {row.getValue('status') === status.ACTIVE ? (
              <div className="text-success">{t('common.page.table.active')}</div>
            ) : (
              <div className="text-destructive-text">{t('common.page.table.blocked')}</div>
            )}
          </div>
        );
      },
      enableHiding: false,
    },
    {
      id: 'createdDate',
      accessorKey: 'createdDate',
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title={t('configurationManagement.charges.chargeSubCategory.table.createdDate')}
        />
      ),
      cell: ({ row }) => (
        <div className="whitespace-nowrap">{formatDate(Number(row.getValue('createdDate')), 'YYYY/MM/DD')}</div>
      ),
      meta: {
        label: 'Created Date',
      },
      enableHiding: false,
      enableColumnFilter: true,
    },
    {
      id: 'actions',
      accessorKey: 'actions',
      header: ({ column }) => (
        <DataTableColumnHeader
          className="whitespace-nowrap"
          column={column}
          title={t('common.page.tableColumn.action')}
        />
      ),
      enableSorting: false,
      enableHiding: false,
      cell: ({ row }) => (
        <div className="flex gap-4 items-center">
          {row.getValue('status') === status.ACTIVE && (
            <EditIcon
              className="text-foreground-subtle size-4 cursor-pointer"
              onClick={() =>
                onOpen({
                  title: 'Manage Commission',
                  okText: t('common.page.modal.submit'),
                  size: 'lg',
                  modalTitle: 'configurationManagement.charges.assignChargeModal.manageCharge',
                  component: (
                    <ManageChargeSubCategoryContainer
                      categoryId={categoryId}
                      agentType={agentType}
                      chargeSubCategoryId={row.getValue('chargeSubCategoryId')}
                    />
                  ),
                })
              }
            />
          )}
          <Button
            variant={'ghost'}
            className="p-0"
            onClick={() => {
              const isActive = row.getValue('status') === status.ACTIVE;
              openConfirmation({
                title: t('common.page.modal.confirm'),
                description: isActive ? t('common.page.modal.blockMessage') : t('common.page.modal.unblockMessage'),
                cancelLabel: t('common.page.modal.no'),
                actionLabel: t('common.page.modal.submit'),
                size: 'lg',
                onAction: async () => {
                  await toggleStatus({
                    chargeSubCategoryId: row.original.chargeSubCategoryId,
                    chargeCategoryId: row.original.chargeCategoryId,
                    status: row.getValue('status') === status.ACTIVE ? status.BLOCK : status.ACTIVE,
                    agentType: agentType,
                  });
                },
                onCancel: () => {},
                variant: 'warning',
              });
            }}
          >
            {row.getValue('status') === status.ACTIVE ? (
              <LockCloseIcon className="text-destructive size-4 " />
            ) : (
              <LockOpenIcon className="text-link size-4 " />
            )}
          </Button>
          {row.getValue('status') === status.ACTIVE && (
            <DeleteIcon
              className="text-destructive size-4 cursor-pointer"
              onClick={() => {
                openConfirmation({
                  title: t('common.page.modal.confirm'),
                  description: t('common.page.modal.deleteItemMessage'),
                  cancelLabel: t('common.page.modal.no'),
                  actionLabel: t('common.page.modal.submit'),
                  size: 'lg',
                  onAction: async () => {
                    await toggleStatus({
                      chargeSubCategoryId: row.original.chargeSubCategoryId,
                      chargeCategoryId: row.original.chargeCategoryId,
                      status: status.DELETE,
                      agentType: agentType,
                    });
                  },
                  onCancel: () => {},
                  variant: 'destructive',
                });
              }}
            />
          )}
        </div>
      ),
      size: 40,
    },
  ];
}
